import React, { useState, useCallback, useEffect } from 'react';
import ContentBox from '../components/ContentBox';
import FONTS from '../data/fonts.json';

const FONT_BASE_PATH = '/img/fontgen';
const STORAGE_KEY = 'fontgen_cache_v2';
const CATEGORY_ORDER = ['All', 'Classic', 'Catalogue', 'Seasonal', 'Habbo', 'Redesigned', 'Misc'];
const ABC_TEXT = 'frankly';

function charToGlyph(char, filenamePattern, folder) {
    if (char === ' ') return { char: ' ', src: `${FONT_BASE_PATH}/${folder}/+.gif`, draw: true };
    const lower = char.toLowerCase();
    if (lower >= 'a' && lower <= 'z') {
        const filename = filenamePattern === 'letter1' ? `${lower}1.gif` : `${lower}.gif`;
        return { char: lower, src: `${FONT_BASE_PATH}/${folder}/${filename}`, draw: true };
    }
    return null;
}

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

async function generateFontImage(text, folder, filenamePattern, offset = 0, voffset = 0, zflip = false, letterVOffsets = {}) {
    const glyphs = text.split('').map((char) => charToGlyph(char, filenamePattern, folder));
    const loadedImgs = await Promise.all(
        glyphs.map((g) => (g?.src ? loadImage(g.src) : Promise.resolve(null)))
    );
    if (!loadedImgs.some(Boolean)) return null;

    let calcWidth = 0;
    let currentX = 0;
    let minY = 0;
    let maxY = 0;
    let visibleIndex = 0;
    const placements = [];

    loadedImgs.forEach((img, i) => {
        const glyph = glyphs[i];
        if (!img || !glyph) return;

        const progressiveVOffset = visibleIndex * voffset;
        const perLetterVOffset = Number(letterVOffsets?.[glyph.char] || 0);
        const y = progressiveVOffset + perLetterVOffset;

        placements.push({
            img,
            glyph,
            x: currentX,
            y,
            order: visibleIndex
        });

        calcWidth = Math.max(calcWidth, currentX + img.naturalWidth);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y + img.naturalHeight);

        currentX += img.naturalWidth + offset;
        visibleIndex += 1;
    });

    if (calcWidth === 0 || maxY - minY === 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = calcWidth;
    canvas.height = maxY - minY;
    const ctx = canvas.getContext('2d');

    const drawList = zflip ? [...placements].reverse() : placements;

    drawList.forEach(({ img, glyph, x, y }) => {
        if (glyph.draw) ctx.drawImage(img, x, y - minY);
    });

    return canvas.toDataURL('image/png');
}

function downloadDataURL(dataURL, filename) {
    const a = document.createElement('a');
    a.href = dataURL;
    a.download = filename;
    a.click();
}

function FontGenerator() {
    const [inputText, setInputText] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generatedText, setGeneratedText] = useState('');
    const [results, setResults] = useState({});
    const [abcResults, setAbcResults] = useState({});
    const [abcLoading, setAbcLoading] = useState(true);
    const [selectedFont, setSelectedFont] = useState(FONTS[0]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [copyFlash, setCopyFlash] = useState(false);

    const hasGenerated = Object.keys(results).length > 0;
    const selectedDataURL = results[selectedFont?.folder] ?? null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                FONTS.map(async (font) => {
                    const url = await generateFontImage(
                        ABC_TEXT,
                        font.folder,
                        font.filenamePattern,
                        font.offset || 0,
                        font.voffset || 0,
                        font.zflip || false,
                        font.letterVOffsets || {}
                    );
                    return [font.folder, url];
                })
            );
            if (!cancelled) {
                setAbcResults(Object.fromEntries(entries));
                setAbcLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        try {
            const cached = localStorage.getItem(STORAGE_KEY);
            if (cached) {
                const { text, results: r, selectedFolder } = JSON.parse(cached);
                if (text && r) {
                    setGeneratedText(text);
                    setInputText(text);
                    setResults(r);
                    const restored = FONTS.find((f) => f.folder === selectedFolder) ?? FONTS[0];
                    setSelectedFont(restored);
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        if (generatedText && Object.keys(results).length > 0) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    text: generatedText,
                    results,
                    selectedFolder: selectedFont?.folder,
                }));
            } catch {}
        }
    }, [generatedText, results, selectedFont]);

    const handleGenerate = useCallback(async () => {
        const text = inputText.trim();
        if (!text || generating) return;

        setGenerating(true);
        setGeneratedText(text);
        setResults({});
        setCopyFlash(false);

        const entries = await Promise.all(
            FONTS.map(async (font) => {
                const url = await generateFontImage(
                    text,
                    font.folder,
                    font.filenamePattern,
                    font.offset || 0,
                    font.voffset || 0,
                    font.zflip || false,
                    font.letterVOffsets || {}
                );
                return [font.folder, url];
            })
        );

        setResults(Object.fromEntries(entries));
        setGenerating(false);
    }, [inputText, generating]);

    const handleClear = useCallback(() => {
        setInputText('');
        setGeneratedText('');
        setResults({});
        setCopyFlash(false);
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }, []);

    const handleCopy = useCallback(async () => {
        if (!selectedDataURL) return;
        try {
            const res = await fetch(selectedDataURL);
            const blob = await res.blob();
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        } catch {
            try { await navigator.clipboard.writeText(selectedDataURL); } catch {}
        }
        setCopyFlash(true);
        setTimeout(() => setCopyFlash(false), 1500);
    }, [selectedDataURL]);

    const handleSave = useCallback(() => {
        if (!selectedDataURL || !selectedFont) return;
        const safeText = (generatedText || 'text').replace(/[^a-z0-9]/gi, '_').slice(0, 32);
        const safeName = selectedFont.name.toLowerCase().replace(/\s+/g, '_');
        downloadDataURL(selectedDataURL, `${safeName}_${safeText}.png`);
    }, [selectedDataURL, selectedFont, generatedText]);

    const visibleFonts = activeCategory === 'All'
        ? FONTS
        : FONTS.filter((f) => f.category === activeCategory);

    return (
        <>
            <style>{`
                .habbo-scroll::-webkit-scrollbar             { height: 16px; width: 16px; }
                .habbo-scroll::-webkit-scrollbar-track       { background: #cfcfcf; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb       { background: #8f8f8f; border: 1px solid #000; }
                .habbo-scroll::-webkit-scrollbar-thumb:hover { background: #7a7a7a; }

                .bg-checker {
                    background-image:
                        linear-gradient(45deg, #ddd 25%, transparent 25%),
                        linear-gradient(-45deg, #ddd 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #ddd 75%),
                        linear-gradient(-45deg, transparent 75%, #ddd 75%);
                    background-size: 8px 8px;
                    background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
                    background-color: #ebebeb;
                }

                .font-thumb {
                    cursor: pointer;
                    border-radius: 4px;
                    border: 2px solid transparent;
                    transition: border-color 0.08s, background 0.08s;
                }
                .font-thumb:hover  { background: rgba(255,255,255,0.65); }
                .font-thumb.active { border-color: #800000; background: rgba(255,255,255,0.9); }
            `}</style>

            <div className="w-full min-h-screen p-4 lg:p-6">
                <div className="w-full mx-auto flex flex-col">
                    <ContentBox theme="default" tint="maroon" header="Font Generator">
                        <div className="flex flex-col gap-8">
                            <div className="flex flex-wrap gap-2 items-stretch">
                                <input
                                    type="text"
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                                    maxLength={80}
                                    placeholder="Type something…"
                                    className="flex-1 min-w-0 px-3 py-2 border-2 border-gray-400  bg-white font-['Jersey_10'] text-gray-900 text-xl focus:outline-none focus:border-maroon transition-colors"
                                />
                                <button
                                    onClick={handleGenerate}
                                    disabled={!inputText.trim() || generating}
                                    className="px-6 py-2 bg-maroon text-white  hover:opacity-90 transition-opacity font-['Jersey_10'] uppercase text-xl tracking-wide disabled:opacity-50 flex-shrink-0"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                >
                                    {generating ? 'Generating…' : 'Generate'}
                                </button>
                                <button
                                    onClick={handleClear}
                                    className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-600 border border-black/20  font-['Jersey_10'] uppercase text-xl tracking-wide transition-colors flex-shrink-0"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.1)' }}
                                >
                                    Clear
                                </button>
                            </div>

                            <div className="flex flex-col gap-2 relative">
                                <div className="font-['Jersey_10'] text-xl text-gray-800 uppercase tracking-wide border-b border-gray-200 pb-1">
                                    {selectedFont?.name ?? 'Preview'}
                                </div>
                                <div
                                    className="bg-checker border border-gray-400  overflow-x-auto habbo-scroll flex items-center justify-center"
                                    style={{ minHeight: '120px' }}
                                >
                                    <div className="flex items-center justify-center p-6" style={{ minWidth: 'max-content' }}>
                                        {generating ? (
                                            <span className="font-['Jersey_10'] uppercase text-gray-400 text-xl animate-pulse">
                                                Generating…
                                            </span>
                                        ) : selectedDataURL ? (
                                            <img
                                                src={selectedDataURL}
                                                alt={`${selectedFont?.name} preview`}
                                                draggable
                                                style={{ imageRendering: 'pixelated', display: 'block' }}
                                            />
                                        ) : (
                                            <span className="font-['Jersey_10'] uppercase text-gray-400 italic text-xl">
                                                {hasGenerated ? 'No glyphs for this font' : 'Enter text above and hit Generate'}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex gap-2 justify-end mt-2 z-10 absolute right-0 -bottom-12">
                                    <button
                                        onClick={handleCopy}
                                        className="px-4 py-1.5 text-lg font-['Jersey_10'] uppercase bg-gray-200 hover:bg-gray-300 border border-black/20  transition-colors"
                                        style={{ boxShadow: '0 1px 0 0 rgba(0,0,0,0.15)' }}
                                    >
                                        {copyFlash ? '✓ Copied!' : 'Copy PNG'}
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="px-4 py-1.5 text-lg font-['Jersey_10'] uppercase bg-gray-200 hover:bg-gray-300 border border-black/20  transition-colors"
                                        style={{ boxShadow: '0 1px 0 0 rgba(0,0,0,0.15)' }}
                                    >
                                        Save PNG
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2 lg:mt-0 mt-5">
                                <div className="font-['Jersey_10']  uppercase tracking-wide border-b border-gray-200 pb-1">
                                    <span className="text-xl text-gray-800">Fonts Library</span> <span className="text-md text-gray-500 ml-2">[Note: Some overlapping fonts are not perfect and may need manual editing - e.g. Habbo (Old)]</span>
                                </div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {CATEGORY_ORDER.map((cat) => {
                                        const count = cat === 'All'
                                            ? FONTS.length
                                            : FONTS.filter((f) => f.category === cat).length;
                                        return (
                                            <button
                                                key={cat}
                                                onClick={() => setActiveCategory(cat)}
                                                className={`px-3 py-1 text-lg font-['Jersey_10'] uppercase border  transition-colors ${
                                                    activeCategory === cat
                                                        ? 'bg-white border-black/25 text-gray-900 shadow-sm'
                                                        : 'bg-gray-100 border-black/10 text-gray-500 hover:bg-gray-200'
                                                }`}
                                            >
                                                {cat}
                                                <span className="ml-1 opacity-60">({count})</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div
                                    className="habbo-scroll overflow-y-auto border border-gray-300 bg-gray-50/60 h-[350px]"
                                >
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 p-3">
                                        {visibleFonts.map((font) => {
                                            const abc = abcResults[font.folder];
                                            const isSelected = selectedFont?.folder === font.folder;
                                            return (
                                                <div
                                                    key={font.folder}
                                                    onClick={() => setSelectedFont(font)}
                                                    title={font.name}
                                                    className={`font-thumb p-2 flex flex-col items-center gap-3 ${isSelected ? 'active' : ''}`}
                                                >
                                                    <div className="bg-checker w-full flex items-center justify-center p-2 overflow-hidden habbo-scroll h-[100px]">
                                                        {abcLoading || !abc ? (
                                                            <span className="text-gray-300 font-['Jersey_10'] text-lg">…</span>
                                                        ) : (
                                                            <img
                                                                src={abc}
                                                                alt={font.name}
                                                                style={{
                                                                    imageRendering: 'pixelated',
                                                                    display: 'block'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <span className="font-['Jersey_10'] uppercase text-center text-gray-700 w-full truncate leading-tight text-lg">
                                                        {font.name}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <p className="font-['Jersey_10'] uppercase text-md text-right text-gray-500">
                                    We're still gathering fonts, if you have any we're missing, please let us know!
                                </p>
                            </div>
                        </div>
                    </ContentBox>
                </div>
            </div>
        </>
    );
}

export default FontGenerator;